import React from "react";
import * as api from "../services/api";
import { useLoaderData, useRevalidator } from "react-router";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { formData, FormErrorList, useFormAction } from "../form";
import ButtonLoader from "../components/ButtonLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";

export async function loader({ params, request }) {
  const res = await api.loadCalendarEditData(
    { id: params.id },
    api.contextFromRequest(request),
  );
  if (res.data.calendar == null) {
    throw new Response("Not Found", { status: 404, statusText: "Not Found" });
  }
  return res;
}

function BasicInfoDialog({ onClose, calendar }) {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const { loading, errors, onSubmit } = useFormAction(
    (e) => api.patchCalendar(formData(e.currentTarget)),
    () => {
      revalidator.revalidate();
      onClose();
    },
  );

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>{t("resources.edit")}</DialogTitle>
      <form onSubmit={onSubmit}>
        <input type="hidden" name="id" value={calendar.id} />
        <DialogContent>
          <Box display="flex" flexDirection="column" gap="1rem">
            <FormControlLabel
              name="visible"
              control={<Checkbox defaultChecked={calendar.visible} />}
              label={t("resources.visible")}
            />
            <FormErrorList formErrors={errors} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            {t("form.cancel")}
          </Button>
          <Button variant="contained" type="submit" disabled={loading}>
            {loading && <ButtonLoader />}
            {t("form.submit")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function Info({ label, value }) {
  return (
    <Box display="flex" alignItems="center" gap="0.5rem">
      <strong>{label}:</strong> {value}
    </Box>
  );
}

function BasicInfo({ resource }) {
  const { t } = useTranslation();
  const [editResource, setEditResource] = React.useState(null);

  return (
    <>
      <Box display="flex" gap="1rem">
        <Box>
          <Info
            label={t("resources.visible")}
            value={
              <FontAwesomeIcon
                icon={resource.visible ? faEye : faEyeSlash}
                size="lg"
              />
            }
          />
        </Box>
        <IconButton onClick={() => setEditResource(resource)}>
          <FontAwesomeIcon icon={faPenToSquare} />
        </IconButton>
      </Box>
      {editResource && (
        <BasicInfoDialog
          onClose={() => setEditResource(null)}
          calendar={resource}
        />
      )}
    </>
  );
}

export default function ResourceEdit() {
  const loaderData = useLoaderData();
  const resource = loaderData.data.calendar;

  return (
    <>
      <h1>
        {resource.name} ({resource.location?.name})
      </h1>
      <BasicInfo resource={resource} />
    </>
  );
}
